import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, GBP, fmtDate, fmtDateTime } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, KeyRound, Pencil, ArrowLeftRight, UserX, UserCheck, Trash2, History } from "lucide-react";

function PayStatus({ s }) {
  const map = { PAID: "#34D399", DUE: "#D4AF37", OVERDUE: "#F59E0B", FAILED: "#F87171" };
  return <span className="text-[0.65rem] uppercase tracking-wider px-2 py-0.5 rounded-sm" style={{ color: map[s] || "#8C867A", background: `${map[s] || "#8C867A"}18` }}>{s}</span>;
}

export default function MemberDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [benches, setBenches] = useState([]);
  const [newTemp, setNewTemp] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [targetBench, setTargetBench] = useState("");
  const [edit, setEdit] = useState({});

  const loadBenches = () => api.get("/benches").then((r) => setBenches(r.data)).catch(() => {});
  const load = () => api.get(`/members/${id}`).then((r) => {
    setD(r.data);
    const m = r.data.member;
    setEdit({
      name: m.name, business_name: m.business_name || "", email: m.email, phone: m.phone || "",
      emergency_contact: m.emergency_contact || "", start_date: (m.start_date || "").slice(0, 10),
      next_payment_date: (m.next_payment_date || "").slice(0, 10), monthly_fee: m.monthly_fee,
    });
  }).catch(() => navigate("/members"));
  useEffect(() => { load(); loadBenches(); }, [id]); // eslint-disable-line

  const resetPw = async () => {
    try { const { data } = await api.post(`/members/${id}/reset-password`); setNewTemp(data.temporary_password); toast.success("Password reset — share the new temporary password"); }
    catch { toast.error("Failed to reset password"); }
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/members/${id}`, {
        ...edit, monthly_fee: Number(edit.monthly_fee),
        start_date: edit.start_date ? new Date(edit.start_date).toISOString() : null,
        next_payment_date: edit.next_payment_date ? new Date(edit.next_payment_date).toISOString() : null,
      });
      toast.success("Member updated"); setEditOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const reassign = async () => {
    if (!targetBench) return toast.error("Choose a bench");
    try { await api.post(`/members/${id}/reassign-bench`, { bench_number: targetBench }); toast.success("Bench reassigned"); setReassignOpen(false); load(); loadBenches(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deactivate = async () => {
    if (!window.confirm("Deactivate this membership? Their bench is freed and they leave the active bench count. All payment, deposit, trade-job and document records are preserved, and the deposit liability remains recorded until formally refunded.")) return;
    try { await api.post(`/members/${id}/deactivate`, { reason: "" }); toast.success("Membership deactivated"); load(); loadBenches(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const reactivate = async () => {
    try { await api.post(`/members/${id}/reactivate`, {}); toast.success("Membership reactivated"); load(); loadBenches(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async () => {
    if (!window.confirm("Permanently DELETE this member? Allowed only for test/demo profiles with no financial, trade-job or document history. This cannot be undone.")) return;
    try { await api.delete(`/members/${id}`); toast.success("Member deleted"); navigate("/members"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!d) return <div className="text-[#8C867A]">Loading…</div>;
  const m = d.member;
  const deposit = d.deposits[0];
  const isActive = m.status === "ACTIVE";
  const inputCls = "w-full panel rounded-sm px-3 py-2.5 text-sm outline-none focus:border-[#C5A059]";
  const btnCls = "panel flex items-center gap-1.5 px-4 py-2.5 rounded-sm text-sm text-[#C2BEB5] hover:border-[#C5A059] transition-colors";

  return (
    <div className="max-w-4xl">
      <button onClick={() => navigate("/members")} className="flex items-center gap-1 text-xs text-[#8C867A] hover:text-[#E2DFD7] mb-4"><ChevronLeft size={14} /> Members</button>
      <div className="flex items-center gap-4 mb-7 rise">
        <div className="w-16 h-16 rounded-sm flex items-center justify-center font-serif text-2xl" style={{ background: "#1A1A1E", color: "#D4AF37" }}>
          {m.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
        </div>
        <div>
          <div className="eyebrow mb-1">{(isActive ? m.bench_number : m.prev_bench_number) ? `${isActive ? m.bench_number : m.prev_bench_number} · ` : ""}<span style={{ color: isActive ? "#34D399" : "#F59E0B" }}>{m.status}</span></div>
          <h1 className="font-serif text-4xl text-[#F5F3EF]">{m.name}</h1>
          <div className="text-sm text-[#8C867A]">{m.business_name}</div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div className="panel rounded-sm p-5">
          <div className="eyebrow mb-3">Contact & Membership</div>
          <div className="space-y-2.5 text-sm">
            {[["Email", m.email], ["Phone", m.phone || "—"], ["Start Date", fmtDate(m.start_date)], ["Next Payment", fmtDate(m.next_payment_date)], ["Monthly Fee", GBP(m.monthly_fee)], ["Emergency Contact", m.emergency_contact || "—"]].map(([l, v]) => (
              <div key={l} className="flex justify-between gap-4"><span className="text-[#8C867A]">{l}</span><span className="text-[#E2DFD7] text-right">{v}</span></div>
            ))}
          </div>
        </div>
        <div className="panel rounded-sm p-5" style={{ borderColor: "#3E382A" }}>
          <div className="eyebrow mb-3">Security Deposit — Held in Trust</div>
          <div className="font-serif text-4xl brass-text mb-1">{GBP(m.security_deposit)}</div>
          <div className="text-xs text-[#8C867A] mb-4">Status: {deposit?.status || m.deposit_status} · Recorded as a liability, not revenue{!isActive ? " · retained after deactivation" : ""}.</div>
          <div className="pt-4 border-t hairline text-sm">
            <div className="flex justify-between"><span className="text-[#8C867A]">Joining payment</span><span className="font-mono text-[#E2DFD7]">{GBP(m.security_deposit + m.monthly_fee)}</span></div>
            <div className="text-[0.65rem] text-[#8C867A] mt-1">{GBP(m.security_deposit)} deposit + {GBP(m.monthly_fee)} first month</div>
          </div>
        </div>
      </div>

      {newTemp && (
        <div className="panel rounded-sm p-4 mb-4 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "#3E382A" }}>
          <div><div className="eyebrow mb-1">New temporary password — shown once</div><div className="font-mono text-lg brass-text" data-testid="reset-temp-password">{newTemp}</div></div>
          <div className="text-xs text-[#8C867A] max-w-[220px]">Member must change this on next sign-in. Share it securely.</div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setEditOpen(true)} data-testid="edit-member-btn" className={btnCls}><Pencil size={14} /> Edit Member</button>
        <button onClick={() => { setTargetBench(""); setReassignOpen(true); }} data-testid="reassign-bench-btn" className={btnCls}><ArrowLeftRight size={14} /> Change Bench</button>
        {isActive
          ? <button onClick={deactivate} data-testid="deactivate-btn" className={btnCls + " hover:!border-[#F59E0B]"}><UserX size={14} /> Deactivate</button>
          : <button onClick={reactivate} data-testid="reactivate-btn" className={btnCls + " hover:!border-[#34D399]"}><UserCheck size={14} /> Reactivate</button>}
        <button onClick={resetPw} data-testid="reset-password-btn" className={btnCls}><KeyRound size={14} /> Reset Password</button>
        <button onClick={del} data-testid="delete-member-btn" className="panel flex items-center gap-1.5 px-4 py-2.5 rounded-sm text-sm text-[#8C867A] hover:border-[#F87171] hover:text-[#F87171] transition-colors"><Trash2 size={14} /> Delete</button>
      </div>

      <div className="panel rounded-sm p-5">
        <div className="eyebrow mb-4">Payment History</div>
        <div className="divide-y" style={{ borderColor: "#28282D" }}>
          {d.payments.length === 0 && <div className="text-sm text-[#8C867A] py-4">No payments recorded.</div>}
          {d.payments.map((p) => (
            <div key={p.id} className="flex items-center justify-between py-3 text-sm">
              <div><div className="text-[#E2DFD7]">{p.period}</div><div className="text-[0.65rem] text-[#8C867A]">{p.type} · {p.method}</div></div>
              <div className="flex items-center gap-3"><span className="font-mono text-[#E2DFD7]">{GBP(p.amount)}</span><PayStatus s={p.status} /></div>
            </div>
          ))}
        </div>
      </div>

      <div className="panel rounded-sm p-5 mt-4" data-testid="member-audit">
        <div className="eyebrow mb-4 flex items-center gap-2"><History size={13} /> Member Audit Trail</div>
        {(!d.audit || d.audit.length === 0) && <div className="text-sm text-[#8C867A]">No admin actions recorded yet.</div>}
        {d.audit?.map((a) => (
          <div key={a.id} className="flex gap-3 py-2.5 border-b hairline last:border-0 text-sm">
            <span className="text-[0.6rem] uppercase tracking-wider brass-text mt-0.5 w-28 shrink-0">{a.action.replace(/_/g, " ")}</span>
            <div className="flex-1"><div className="text-[#E2DFD7]">{a.detail}</div><div className="text-[0.65rem] text-[#8C867A] mt-0.5 num">{a.by} · {fmtDateTime(a.at)}</div></div>
          </div>
        ))}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#131316] border-[#28282D] text-[#F5F3EF] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Edit Member</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3 mt-2">
            <div className="sm:col-span-2"><label className="eyebrow block mb-1.5">Full Name</label><input data-testid="edit-name" value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className={inputCls} /></div>
            <div><label className="eyebrow block mb-1.5">Business Name</label><input value={edit.business_name || ""} onChange={(e) => setEdit({ ...edit, business_name: e.target.value })} className={inputCls} /></div>
            <div><label className="eyebrow block mb-1.5">Email</label><input data-testid="edit-email" value={edit.email || ""} onChange={(e) => setEdit({ ...edit, email: e.target.value })} className={inputCls} /></div>
            <div><label className="eyebrow block mb-1.5">Phone</label><input value={edit.phone || ""} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} className={inputCls} /></div>
            <div><label className="eyebrow block mb-1.5">Emergency Contact</label><input value={edit.emergency_contact || ""} onChange={(e) => setEdit({ ...edit, emergency_contact: e.target.value })} className={inputCls} /></div>
            <div><label className="eyebrow block mb-1.5">Start Date</label><input type="date" value={edit.start_date || ""} onChange={(e) => setEdit({ ...edit, start_date: e.target.value })} className={inputCls} /></div>
            <div><label className="eyebrow block mb-1.5">Next Payment Date</label><input type="date" value={edit.next_payment_date || ""} onChange={(e) => setEdit({ ...edit, next_payment_date: e.target.value })} className={inputCls} /></div>
            <div><label className="eyebrow block mb-1.5">Monthly Fee (£)</label><input type="number" step="0.01" value={edit.monthly_fee ?? ""} onChange={(e) => setEdit({ ...edit, monthly_fee: e.target.value })} className={inputCls + " font-mono"} /></div>
          </div>
          <button onClick={saveEdit} data-testid="save-edit" className="btn-brass w-full py-2.5 rounded-sm text-sm mt-2">Save Changes</button>
        </DialogContent>
      </Dialog>

      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent className="bg-[#131316] border-[#28282D] text-[#F5F3EF] max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Change / Reassign Bench</DialogTitle></DialogHeader>
          <p className="text-sm text-[#8C867A]">Current: {m.bench_number || "—"}. Move to an available bench only.</p>
          <select value={targetBench} onChange={(e) => setTargetBench(e.target.value)} data-testid="reassign-select" className={inputCls + " mt-1"}>
            <option value="">Select bench…</option>
            {benches.map((b) => {
              const taken = b.status === "OCCUPIED" && b.member_id !== m.id;
              return <option key={b.id} value={b.name} disabled={taken}>{b.name}{taken ? " (occupied)" : b.member_id === m.id ? " (current)" : ""}</option>;
            })}
          </select>
          <button onClick={reassign} data-testid="confirm-reassign" className="btn-brass w-full py-2.5 rounded-sm text-sm mt-2">Reassign Bench</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
